import { LISTING_API_URL } from '~/lib/environment';
import { toolboxApi, unwrapResult } from '~/modules/query/base';

/** One MLS feed document from GET /mls (mlsid, _id, mongoData, ...). */
export interface MlsFeed {
  [key: string]: unknown;
  _id?: string;
  mlsid?: string;
}

/** GET /mls */
export type MlsListResult = MlsFeed[];

/** GET /mls/{id}?mlsid={id} — the MLS view configuration. */
export interface MlsViewConfig {
  [key: string]: unknown;
  mongoData?: unknown;
}

/** The editable columns of a feed's realistiq MLS row. Secondary paths and the DB string never reach the UI. */
export interface MlsConfigFields {
  description: string | null;
  contact: string | null;
  phone: string | null;
  feedType: string | null;
  userName: string | null;
  password: string | null;
  feedUrl: string | null;
  rowLimit: number | null;
  listTracEnabled: string | null;
  disclaimer: string | null;
  imagePath: string | null;
  imageUrl: string | null;
  attachmentPath: string | null;
  listingAttrType: string | null;
  listingAttrLocation: string | null;
  listingAttrContent: string | null;
  showOffMarketListings: string | null;
}

/** GET /mls/config/{mlsid}: stored codes come back as-is (any case, NULL or unknown). */
export interface MlsConfig extends MlsConfigFields {
  mlsid: string;
  /** True when a stored path has an unrecognised shape; shown read-only and rejected on write. */
  pathsLocked: { imagePath: boolean; imageUrl: boolean; attachmentPath: boolean };
}

/** POST /mls/config body. */
export interface MlsConfigCreate extends MlsConfigFields {
  mlsid: string;
}

/** GET /mls/AGG/stats */
export interface MlsStats {
  [key: string]: unknown;
  nTot?: number;
}

/** POST /mls body. */
export interface InsertDuplicateMlsRequest {
  mlsid: string;
  newMlsId: string;
}

/** PUT /mls/{mlsid} body. */
export interface UpdateMlsRequest {
  [key: string]: unknown;
  mongoData?: unknown;
}

/** Result of a mutating MLS call (create/update/delete/upload/report). */
export interface MlsMutationResult {
  [key: string]: unknown;
  message?: unknown;
}

export const mlsApi = toolboxApi.injectEndpoints({
  endpoints: (builder) => ({
    getMls: builder.query<MlsListResult, void>({
      query: () => ({ url: '/mls', method: 'GET' }),
      transformResponse: unwrapResult<MlsListResult>,
      providesTags: ['Mls'],
    }),
    getMlsStats: builder.query<MlsStats, void>({
      query: () => ({ url: '/mls/AGG/stats', method: 'GET', timeout: 5000 }),
      transformResponse: unwrapResult<MlsStats>,
      providesTags: ['MlsStats'],
    }),
    getViewConfig: builder.query<MlsViewConfig, string>({
      query: (mlsid) => ({ url: `/mls/${mlsid}?mlsid=${mlsid}`, method: 'GET' }),
      transformResponse: unwrapResult<MlsViewConfig>,
      providesTags: ['MlsView'],
    }),
    insertDuplicateMls: builder.mutation<MlsMutationResult, InsertDuplicateMlsRequest>({
      query: (body) => ({ url: '/mls', method: 'POST', data: body }),
      transformResponse: unwrapResult<MlsMutationResult>,
      invalidatesTags: ['Mls', 'MlsStats'],
    }),
    updateMls: builder.mutation<MlsMutationResult, { mlsid: string; data: UpdateMlsRequest }>({
      query: ({ mlsid, data }) => ({ url: `/mls/${mlsid}`, method: 'PUT', data }),
      transformResponse: unwrapResult<MlsMutationResult>,
      invalidatesTags: ['Mls', 'MlsStats', 'MlsView'],
    }),
    deleteMls: builder.mutation<MlsMutationResult, string>({
      query: (mlsid) => ({ url: `/mls/${mlsid}`, method: 'DELETE' }),
      transformResponse: unwrapResult<MlsMutationResult>,
      invalidatesTags: ['Mls', 'MlsStats', 'MlsConfig'],
    }),
    getMlsConfig: builder.query<MlsConfig, string>({
      query: (mlsid) => ({ url: `/mls/config/${encodeURIComponent(mlsid)}`, method: 'GET' }),
      transformResponse: unwrapResult<MlsConfig>,
      // Tagged on failure too, so a 404 (no row yet) refetches once the row is created.
      providesTags: (_result, _error, mlsid) => [{ type: 'MlsConfig', id: mlsid }],
    }),
    createMlsConfig: builder.mutation<MlsConfig, MlsConfigCreate>({
      query: (data) => ({ url: '/mls/config', method: 'POST', data }),
      transformResponse: unwrapResult<MlsConfig>,
      invalidatesTags: (_result, _error, { mlsid }) => [{ type: 'MlsConfig', id: mlsid }],
    }),
    updateMlsConfig: builder.mutation<MlsConfig, { mlsid: string; data: Partial<MlsConfigFields> }>(
      {
        query: ({ mlsid, data }) => ({
          url: `/mls/config/${encodeURIComponent(mlsid)}`,
          method: 'PUT',
          data,
        }),
        transformResponse: unwrapResult<MlsConfig>,
        invalidatesTags: (_result, _error, { mlsid }) => [{ type: 'MlsConfig', id: mlsid }],
      },
    ),
    uploadLogo: builder.mutation<MlsMutationResult, { mlsid: string; file: Blob }>({
      query: ({ mlsid, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return { url: `/mls/${mlsid}/logo`, method: 'POST', data: formData };
      },
      transformResponse: unwrapResult<MlsMutationResult>,
      invalidatesTags: ['Mls'],
    }),
    trestleReport: builder.mutation<MlsMutationResult, string>({
      // The listing service is absolute; the base query leaves the URL as-is.
      query: (mlsid) => ({
        url: `${LISTING_API_URL}/feed/trestle/${mlsid}/report`,
        method: 'POST',
        data: {},
      }),
      transformResponse: unwrapResult<MlsMutationResult>,
      invalidatesTags: ['Mls'],
    }),
  }),
});

export const {
  useGetMlsQuery,
  useGetMlsStatsQuery,
  useGetViewConfigQuery,
  useInsertDuplicateMlsMutation,
  useUpdateMlsMutation,
  useDeleteMlsMutation,
  useGetMlsConfigQuery,
  useCreateMlsConfigMutation,
  useUpdateMlsConfigMutation,
  useUploadLogoMutation,
  useTrestleReportMutation,
} = mlsApi;
